import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon,
  UserCircleIcon,
  XCircleIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAppData } from '../../contexts/AppDataContext';

const CustomerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { addCustomer, updateCustomer, getCustomerById, fetchAllData } = useAppData();
  
  const [isLoading, setIsLoading] = useState(false);
  const [customer, setCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    wilaya: '',
    company: '',
    nif: '',
    rc: '',
    ai: '',
    notes: '',
    avatar: null,
    avatarPreview: null
  });
  
  // Liste des wilayas d'Algérie
  const wilayas = [
    'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra', 'Béchar', 'Blida', 'Bouira',
    'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret', 'Tizi Ouzou', 'Alger', 'Djelfa', 'Jijel', 'Sétif', 'Saïda',
    'Skikda', 'Sidi Bel Abbès', 'Annaba', 'Guelma', 'Constantine', 'Médéa', 'Mostaganem', 'M\'Sila', 'Mascara', 'Ouargla',
    'Oran', 'El Bayadh', 'Illizi', 'Bordj Bou Arreridj', 'Boumerdès', 'El Tarf', 'Tindouf', 'Tissemsilt', 'El Oued', 'Khenchela',
    'Souk Ahras', 'Tipaza', 'Mila', 'Aïn Defla', 'Naâma', 'Aïn Témouchent', 'Ghardaïa', 'Relizane', 'Timimoun',
    'Bordj Badji Mokhtar', 'Ouled Djellal', 'Béni Abbès', 'In Salah', 'In Guezzam', 'Touggourt', 'Djanet', 'El M\'Ghair', 'El Meniaa'
  ];

  useEffect(() => {
    // Si en mode édition, charger les données du client
    if (isEditMode) {
      setIsLoading(true);
      
      // Récupérer le client par ID
      const existingCustomer = getCustomerById(parseInt(id));
      
      if (existingCustomer) {
        setCustomer(existingCustomer);
      } else {
        toast.error('Client introuvable');
        navigate('/customers');
      }
      
      setIsLoading(false);
    }
  }, [id, isEditMode, getCustomerById, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomer(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier le type et la taille du fichier
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB
    
    if (!validTypes.includes(file.type)) {
      toast.error('Format d\'image non supporté. Utilisez JPG, PNG ou WebP.');
      return;
    }
    
    if (file.size > maxSize) {
      toast.error('Image trop volumineuse. La taille maximale est de 2MB.');
      return;
    }
    
    // Prévisualiser l'image
    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomer(prev => ({
        ...prev,
        avatar: file,
        avatarPreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeAvatar = () => {
    setCustomer(prev => ({
      ...prev,
      avatar: null,
      avatarPreview: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation basique
    if (!customer.firstName.trim() || !customer.lastName.trim()) {
      toast.error('Les nom et prénom sont requis');
      return;
    }
    
    if (customer.email && !isValidEmail(customer.email)) {
      toast.error('L\'adresse e-mail n\'est pas valide');
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (isEditMode) {
        // Mise à jour d'un client existant
        await updateCustomer(customer.id, customer);
        toast.success(`Le client ${customer.firstName} ${customer.lastName} a été mis à jour`);
      } else {
        // Création d'un nouveau client
        const newCustomer = await addCustomer(customer);
        console.log('✅ Customer added successfully:', newCustomer);
        toast.success(`Le client ${customer.firstName} ${customer.lastName} a été créé avec succès`);
      }
      
      // Force a small delay to ensure localStorage is written
      console.log('⏳ Waiting for localStorage sync...');
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Petit délai pour assurer que le contexte est mis à jour avant la navigation
      setTimeout(() => {
        console.log('📍 Navigating back to customers list...');
        navigate('/customers');
      }, 100);
      
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('Erreur lors de l\'enregistrement du client');
    } finally {
      setIsLoading(false);
    }
  };

  // Validation simple d'email
  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/customers')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditMode ? 'Modifier le client' : 'Ajouter un client'}
            </h1>
            <p className="text-white/60 mt-1">
              {isEditMode ? 'Modifier les informations du client' : 'Créez un nouveau client dans votre système'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Colonne principale - Informations du client */}
              <div className="lg:col-span-2 space-y-6">
                {/* Informations personnelles */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Informations personnelles</h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-white font-medium mb-1">
                          Prénom <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={customer.firstName}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Prénom"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="lastName" className="block text-white font-medium mb-1">
                          Nom <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={customer.lastName}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Nom"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-white font-medium mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={customer.email}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="email@exemple.com"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="phone" className="block text-white font-medium mb-1">
                          Téléphone
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={customer.phone}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="+213 XXX XXX XXX"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-white font-medium mb-1">
                        Entreprise
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={customer.company}
                        onChange={handleInputChange}
                        className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nom de l'entreprise"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Adresse */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Adresse</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="address" className="block text-white font-medium mb-1">
                        Adresse
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={customer.address}
                        onChange={handleInputChange}
                        className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Numéro et nom de rue"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="city" className="block text-white font-medium mb-1">
                          Ville
                        </label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={customer.city}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ville"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="postalCode" className="block text-white font-medium mb-1">
                          Code postal
                        </label>
                        <input
                          type="text"
                          id="postalCode"
                          name="postalCode"
                          value={customer.postalCode}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Code postal"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="wilaya" className="block text-white font-medium mb-1">
                          Wilaya
                        </label>
                        <select
                          id="wilaya"
                          name="wilaya"
                          value={customer.wilaya}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Sélectionner une wilaya</option>
                          {wilayas.map((wilaya, index) => (
                            <option key={index} value={wilaya}>{wilaya}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Informations fiscales */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Informations fiscales</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="nif" className="block text-white font-medium mb-1">
                        NIF (Numéro d'Identification Fiscale)
                      </label>
                      <input
                        type="text"
                        id="nif"
                        name="nif"
                        value={customer.nif}
                        onChange={handleInputChange}
                        className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="000000000000000"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="rc" className="block text-white font-medium mb-1">
                          RC (Registre de Commerce)
                        </label>
                        <input
                          type="text"
                          id="rc"
                          name="rc"
                          value={customer.rc}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="RC-00-B-0000"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="ai" className="block text-white font-medium mb-1">
                          AI (Article d'Imposition)
                        </label>
                        <input
                          type="text"
                          id="ai"
                          name="ai"
                          value={customer.ai}
                          onChange={handleInputChange}
                          className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="00-00-0000000"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Notes */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Notes</h2>
                  
                  <div>
                    <textarea
                      id="notes"
                      name="notes"
                      value={customer.notes}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Notes concernant ce client..."
                    ></textarea>
                  </div>
                </div>
              </div>
              
              {/* Colonne latérale - Photo et résumé */}
              <div className="space-y-6">
                {/* Photo */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Photo</h2>
                  
                  <div className="flex flex-col items-center">
                    <div className="relative mb-4">
                      {customer.avatarPreview ? (
                        <>
                          <img
                            src={customer.avatarPreview}
                            alt="Avatar"
                            className="w-32 h-32 rounded-full object-cover border-2 border-white/20"
                          />
                          <button
                            type="button"
                            onClick={removeAvatar}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600 transition-colors"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        </>
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center">
                          <UserCircleIcon className="h-24 w-24 text-white/40" />
                        </div>
                      )}
                    </div>
                    
                    <label className="cursor-pointer bg-white/10 hover:bg-white/20 transition-colors text-white px-4 py-2 rounded-xl">
                      Sélectionner une photo
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleAvatarUpload}
                      />
                    </label>
                    <p className="mt-2 text-sm text-white/60">
                      Format recommandé: PNG, JPG (max 2 MB)
                    </p>
                  </div>
                </div>
                
                {/* Résumé */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Résumé</h2>
                  
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <MapPinIcon className="h-5 w-5 text-white/60 mr-2 mt-0.5" />
                      <span className="text-white">
                        {customer.address && customer.city ? 
                          `${customer.address}, ${customer.city}` : 
                          <span className="text-white/60">Non spécifié</span>
                        }
                      </span>
                    </div>
                    
                    <div className="flex items-start">
                      <BuildingOfficeIcon className="h-5 w-5 text-white/60 mr-2 mt-0.5" />
                      <span className="text-white">
                        {customer.company || <span className="text-white/60">Non spécifié</span>}
                      </span>
                    </div>
                    
                    <div className="flex items-start">
                      <EnvelopeIcon className="h-5 w-5 text-white/60 mr-2 mt-0.5" />
                      <span className="text-white">
                        {customer.email || <span className="text-white/60">Non spécifié</span>}
                      </span>
                    </div>
                    
                    <div className="flex items-start">
                      <PhoneIcon className="h-5 w-5 text-white/60 mr-2 mt-0.5" />
                      <span className="text-white">
                        {customer.phone || <span className="text-white/60">Non spécifié</span>}
                      </span>
                    </div>
                    
                    <div className="flex items-start">
                      <DocumentTextIcon className="h-5 w-5 text-white/60 mr-2 mt-0.5" />
                      <span className="text-white">
                        NIF: {customer.nif || <span className="text-white/60">Non spécifié</span>}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/customers')}
                className="px-6 py-2.5 bg-white/10 hover:bg-white/20 transition-colors text-white rounded-xl"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl flex items-center justify-center"
              >
                {isLoading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isEditMode ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerForm; 